/**
 * Client for the admin Point-in-Time Timeline endpoint (feat-125, Slice B).
 *
 * Contract: worktrees/feat-125/decisions/0001-pit-timeline-endpoint.md
 *   - §D1  endpoint + two mutually-exclusive search paths
 *   - §D2  response schema (this file mirrors it verbatim — the ONLY thing the FE codes against)
 *   - §D8  FE positioning rule (lives in the component, not here)
 *
 * Single endpoint (no /admin/execute-select — the SQL + ALL tz math stay server-side):
 *   GET /api/admin/timeline/point-in-time
 *     ?location_id=<uuid>&at_local=<YYYY-MM-DDTHH:MM:SS>   (time path)
 *   — or —
 *     ?source=<vendor>&vendor_order_id=<id>                (order-id path)
 *
 * The `/api` prefix is the router mount (main.py include_router prefix="/api").
 *
 * INVARIANT (§D2/§D8, AC15): every timestamp the backend returns is PRE-RESOLVED
 * restaurant-local. The FE positions exclusively by `offset_seconds /
 * window.duration_seconds`; it never parses `at_local` for positioning and NEVER
 * touches `opened_at` / UTC. `at_local` strings are display-only.
 *
 * Money / quantity / price come back as JSON numbers already (backend casts
 * float(Decimal) at the response edge, §D6) — the client does NOT re-parseFloat.
 */
import { config } from '../config/environment';
import { authHeader } from './authToken';
import { authedApiFetch } from './tokenRefresh';
import managementService from './managementService';

// ---------- Domain types (mirror ADR 0001 §D2 verbatim) ----------

export interface PitLocation {
  id: string;
  name: string;
  timezone: string; // IANA, e.g. 'America/Los_Angeles' — display only
}

export interface PitMatchedOrder {
  pos_order_id: string;
  source: string;
  vendor_order_id: string;
  state: string; // 'open' | 'closed' | 'canceled'
  is_canceled: boolean;
}

export interface PitAnchor {
  source: 'order_id' | 'time';
  at_local: string; // T in restaurant-local (naive ISO, no offset) — display only
  matched_order: PitMatchedOrder | null; // null when source === 'time'
}

export interface PitWindow {
  start_local: string; // T - 5m (display only)
  end_local: string; // T + 1m (display only)
  t_local: string; // T (display only)
  duration_seconds: number; // ALWAYS 360 (fixed window) — the positioning denominator
  t_offset_seconds: number; // T sits this many seconds into the window (300)
}

export interface PitTranscriptPart {
  id: string;
  at_local: string; // display only
  offset_seconds: number; // seconds from window start — the positioning value
  start_time: number; // seconds from shift start (diagnostic)
  speaker: number; // INT speaker id; 0 = server turn, else customer (existing convention)
  text: string;
}

export interface PitInteraction {
  id: string;
  at_local: string; // display only
  offset_seconds: number;
  start_time: number;
  interaction_type: string; // greeting, server_upsell, missed_upsell, drink_promotion, …
  /** shift_interaction.start_extracted_from_transcript_part_id — badge pins to this part. */
  attributed_transcript_part_id: string | null;
  data: Record<string, unknown> | null; // shift_interaction.data JSON passthrough
}

export interface PitColumn {
  shift_id: string;
  server_id: string;
  server_name: string | null; // NULLIF(TRIM(first+last),''); serverLabel() falls back to id
  shift_created_local: string; // display only
  transcript_parts: PitTranscriptPart[];
  interactions: PitInteraction[];
}

export interface PitAttribution {
  shift_interaction_id: string; // == some column interaction's `id` (hover-pairing key)
  match_rule: string; // metric_1 | metric_2 | metric_3 | drink_promotion | …
  time_diff_seconds: number;
  explanation: string | null;
}

export interface PitLineItem {
  id: string;
  position: number;
  name: string;
  quantity: number | null;
  unit_price: number | null;
  total_money: number | null;
  attribution: PitAttribution[];
}

export interface PitOrder {
  pos_order_id: string;
  source: string;
  vendor_order_id: string;
  at_local: string; // display only
  offset_seconds: number; // positioning value
  state: string;
  is_canceled: boolean;
  is_anchor: boolean;
  total_money: number | null;
  currency: string;
  line_items: PitLineItem[];
}

export interface PitTimelineResponse {
  location: PitLocation;
  anchor: PitAnchor;
  window: PitWindow;
  columns: PitColumn[]; // [] when no live shifts in window (OQ3) — axis + orders still render
  orders: PitOrder[]; // axis ALWAYS rendered even if []
}

/** A location for the required time-path selector — admin sees ALL locations, ALL tenants. */
export interface PitLocationOption {
  id: string;
  name: string;
  tenant_name?: string;
}

// ---------- search params ----------

export type PitSearchParams =
  | { mode: 'time'; locationId: string; atLocal: string }
  | { mode: 'order'; source: string; vendorOrderId: string };

// ---------- errors ----------

export class PitNotFoundError extends Error {
  constructor(detail: string) {
    super(detail || 'Not found');
    this.name = 'PitNotFoundError';
  }
}

export class PitValidationError extends Error {
  constructor(detail: string) {
    super(detail || 'Invalid request');
    this.name = 'PitValidationError';
  }
}

// ---------- HTTP plumbing (mirrors posInspectorService.ts) ----------

function backendUrl(path: string): string {
  const protocol =
    config.environment === 'fake' || config.environment === 'local' ? 'http' : 'https';
  return `${protocol}://${config.restaurantBackendHost}${path}`;
}

async function api<T>(path: string): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...authHeader(),
  };
  const resp = await authedApiFetch(backendUrl(path), { method: 'GET', headers });
  if (resp.status === 404) {
    throw new PitNotFoundError(await detailOf(resp, 'Order or location not found'));
  }
  if (resp.status === 422) {
    throw new PitValidationError(await detailOf(resp, 'Invalid search parameters'));
  }
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(`Timeline API ${path} ${resp.status}: ${text}`);
  }
  return resp.json();
}

async function detailOf(resp: Response, fallback: string): Promise<string> {
  try {
    const body = await resp.json();
    const d = (body as { detail?: unknown })?.detail;
    return typeof d === 'string' ? d : fallback;
  } catch {
    return fallback;
  }
}

// ---------- public API ----------

/** Fetch the point-in-time timeline payload for either search path (§D1). */
export async function fetchPitTimeline(params: PitSearchParams): Promise<PitTimelineResponse> {
  if (config.environment === 'fake') {
    return fakeTimeline(params);
  }
  const q = new URLSearchParams();
  if (params.mode === 'time') {
    q.set('location_id', params.locationId);
    q.set('at_local', params.atLocal);
  } else {
    q.set('source', params.source);
    q.set('vendor_order_id', params.vendorOrderId);
  }
  return api<PitTimelineResponse>(`/api/admin/timeline/point-in-time?${q.toString()}`);
}

/**
 * ALL locations across ALL tenants for the required time-path selector (AC5).
 * Reuses the admin-wide /api/management/locations list (same source the
 * Locations admin index uses); admin is intentionally fleet-wide.
 */
export async function listAllLocations(): Promise<PitLocationOption[]> {
  if (config.environment === 'fake') {
    return [
      { id: 'loc-llp', name: 'L&L Hawaiian BBQ — Pearlridge', tenant_name: 'Ladle & Leaf' },
      { id: 'loc-grove-ny', name: 'The Grove — Manhattan', tenant_name: 'Grove Café' },
      { id: 'loc-fremont', name: 'Bespoke Diner — Fremont', tenant_name: 'Bespoke' },
    ];
  }
  const r = await managementService.getLocations();
  if (!r.success || !r.locations) {
    throw new Error(r.error ?? 'Failed to load locations');
  }
  return r.locations.map((l: Record<string, unknown>) => ({
    id: String(l.id ?? l.location_id),
    name: String(l.name ?? l.location_name ?? `Location ${l.id ?? l.location_id}`),
    tenant_name:
      (l.tenant_name as string | undefined) ?? (l.tenant as string | undefined) ?? undefined,
  }));
}

// ---------- fake mode ----------
// Renders the happy path (two columns + an anchored order with attribution),
// the empty-columns case (?pit_state=empty), and the canceled-anchor case
// (order path with vendor id 'CANCELED') so QA can exercise OQ1/OQ3 without a backend.

function fakeStateOverride(): string | null {
  try {
    return new URLSearchParams(window.location.search).get('pit_state');
  } catch {
    return null;
  }
}

const FAKE_WINDOW: PitWindow = {
  start_local: '2026-06-29T12:25:00',
  end_local: '2026-06-29T12:31:00',
  t_local: '2026-06-29T12:30:00',
  duration_seconds: 360,
  t_offset_seconds: 300,
};

function fakeColumns(): PitColumn[] {
  return [
    {
      shift_id: 'shift-1',
      server_id: 'srv-jane',
      server_name: 'Jane Doe',
      shift_created_local: '2026-06-29T11:00:00',
      transcript_parts: [
        { id: 'tp-1', at_local: '2026-06-29T12:26:14', offset_seconds: 74, start_time: 5174, speaker: 0, text: 'Hi, welcome in! What can I get started for you?' },
        { id: 'tp-2', at_local: '2026-06-29T12:26:40', offset_seconds: 100, start_time: 5200, speaker: 1, text: "I'll take the chicken plate." },
        { id: 'tp-3', at_local: '2026-06-29T12:27:05', offset_seconds: 125, start_time: 5225, speaker: 0, text: 'Want to make it a steak shawarma for two dollars more? It pairs great.' },
        { id: 'tp-4', at_local: '2026-06-29T12:29:30', offset_seconds: 270, start_time: 5370, speaker: 1, text: 'Sure, the shawarma sounds good.' },
      ],
      interactions: [
        { id: 'int-greet', at_local: '2026-06-29T12:26:14', offset_seconds: 74, start_time: 5174, interaction_type: 'greeting', attributed_transcript_part_id: 'tp-1', data: {} },
        { id: 'int-up1', at_local: '2026-06-29T12:27:05', offset_seconds: 125, start_time: 5225, interaction_type: 'server_upsell', attributed_transcript_part_id: 'tp-3', data: { quality_level: 3 } },
      ],
    },
    {
      shift_id: 'shift-2',
      server_id: 'srv-bill',
      server_name: 'Bill Tran',
      shift_created_local: '2026-06-29T10:30:00',
      transcript_parts: [
        { id: 'tp-b1', at_local: '2026-06-29T12:28:10', offset_seconds: 190, start_time: 6490, speaker: 0, text: 'I can ring you up over here whenever you are ready.' },
        { id: 'tp-b2', at_local: '2026-06-29T12:30:20', offset_seconds: 320, start_time: 6620, speaker: 0, text: 'Would you like a Dubai chocolate bar with that?' },
      ],
      interactions: [
        { id: 'int-up2', at_local: '2026-06-29T12:30:20', offset_seconds: 320, start_time: 6620, interaction_type: 'drink_promotion', attributed_transcript_part_id: 'tp-b2', data: {} },
      ],
    },
  ];
}

function fakeOrders(canceledAnchor: boolean): PitOrder[] {
  return [
    {
      pos_order_id: 'ord-anchor',
      source: 'square',
      vendor_order_id: canceledAnchor ? 'CANCELED' : 'sq-ORDER-001',
      at_local: '2026-06-29T12:30:00',
      offset_seconds: 300,
      state: canceledAnchor ? 'canceled' : 'closed',
      is_canceled: canceledAnchor,
      is_anchor: true,
      total_money: 24.5,
      currency: 'USD',
      line_items: [
        {
          id: 'li-1', position: 0, name: 'Steak Shawarma Plate', quantity: 1, unit_price: 14.5, total_money: 14.5,
          attribution: [
            { shift_interaction_id: 'int-up1', match_rule: 'metric_1', time_diff_seconds: -175, explanation: "Jane offered the steak shawarma upsell 175s before this item was rung up." },
          ],
        },
        {
          id: 'li-2', position: 1, name: 'Dubai Chocolate Bar', quantity: 1, unit_price: 6.0, total_money: 6.0,
          attribution: [
            { shift_interaction_id: 'int-up2', match_rule: 'drink_promotion', time_diff_seconds: 20, explanation: "Bill promoted the Dubai chocolate bar just before this item was added." },
          ],
        },
        { id: 'li-3', position: 2, name: 'Fountain Drink', quantity: 1, unit_price: 4.0, total_money: 4.0, attribution: [] },
      ],
    },
    {
      pos_order_id: 'ord-2',
      source: 'square',
      vendor_order_id: 'sq-ORDER-002',
      at_local: '2026-06-29T12:27:40',
      offset_seconds: 160,
      state: 'closed',
      is_canceled: false,
      is_anchor: false,
      total_money: 9.25,
      currency: 'USD',
      line_items: [
        { id: 'li-4', position: 0, name: 'Side Salad', quantity: 1, unit_price: 9.25, total_money: 9.25, attribution: [] },
      ],
    },
  ];
}

function fakeTimeline(params: PitSearchParams): PitTimelineResponse {
  const empty = fakeStateOverride() === 'empty';
  const canceledAnchor =
    params.mode === 'order' && params.vendorOrderId.trim().toUpperCase() === 'CANCELED';
  const anchor: PitAnchor =
    params.mode === 'order'
      ? {
          source: 'order_id',
          at_local: '2026-06-29T12:30:00',
          matched_order: {
            pos_order_id: 'ord-anchor',
            source: params.source || 'square',
            vendor_order_id: params.vendorOrderId || 'sq-ORDER-001',
            state: canceledAnchor ? 'canceled' : 'closed',
            is_canceled: canceledAnchor,
          },
        }
      : { source: 'time', at_local: '2026-06-29T12:30:00', matched_order: null };
  return {
    location: { id: 'loc-llp', name: 'L&L Hawaiian BBQ — Pearlridge', timezone: 'Pacific/Honolulu' },
    anchor,
    window: FAKE_WINDOW,
    columns: empty ? [] : fakeColumns(),
    orders: fakeOrders(canceledAnchor),
  };
}
